import {
  importCatalogListPage,
  importCatalogPage,
  importHomePage,
  importOrdersPage,
  importProcurementPage,
} from './lazyPages';

/** Path → dynamic import (must match `App.tsx` lazy page chunks). */
const ROUTE_CHUNKS: { prefix: string; load: () => Promise<unknown> }[] = [
  { prefix: '/settings/shipping', load: () => import('../pages/FulfillmentSettingsPage') },
  { prefix: '/settings/payment', load: () => import('../pages/FulfillmentSettingsPage') },
  { prefix: '/settings/fulfillment', load: () => import('../pages/FulfillmentSettingsPage') },
  { prefix: '/settings/data-sources', load: () => import('../pages/ExternalCatalogDataSourcePage') },
  { prefix: '/crm/flows/', load: () => import('../pages/LeadFlowDetailPage') },
  { prefix: '/crm/flows', load: () => import('../pages/LeadFlowsPage') },
  { prefix: '/roles/', load: () => import('../pages/RoleDetailPage') },
  { prefix: '/roles', load: () => import('../pages/RolesPage') },
  { prefix: '/users', load: () => import('../pages/UsersPage') },
  { prefix: '/search', load: () => import('../pages/SearchPage') },
  { prefix: '/documents', load: () => import('../pages/DocumentsPage') },
  { prefix: '/customer', load: () => import('../pages/CustomerPage') },
  { prefix: '/procurement/quotes', load: () => import('../pages/QuotesPage') },
  { prefix: '/procurement', load: importProcurementPage },
  { prefix: '/sales/quotes', load: () => import('../pages/SaleQuotesPage') },
  { prefix: '/orders', load: importOrdersPage },
  { prefix: '/catalog/types/', load: () => import('../pages/ProductTypeDetailPage') },
  { prefix: '/catalog/types', load: () => import('../pages/ProductTypesPage') },
  { prefix: '/catalog/products', load: importCatalogPage },
  { prefix: '/catalog', load: importCatalogListPage },
  { prefix: '/inventory/lots', load: () => import('../pages/InventoryLotsPage') },
  { prefix: '/inventory/locations', load: () => import('../pages/LocationsPage') },
  { prefix: '/inventory', load: () => import('../pages/InventoryPage') },
  { prefix: '/leads', load: () => import('../pages/LeadsPage') },
  { prefix: '/', load: importHomePage },
];

const inflight = new Map<string, Promise<unknown>>();

function normalizePath(pathname: string): string {
  return pathname.split('?')[0]?.split('#')[0] ?? '/';
}

function matchChunk(pathname: string) {
  const path = normalizePath(pathname);
  for (const entry of ROUTE_CHUNKS) {
    if (entry.prefix === '/') {
      if (path === '/') return entry;
      continue;
    }
    if (path === entry.prefix || path.startsWith(`${entry.prefix}/`)) return entry;
  }
  return ROUTE_CHUNKS.find((e) => e.prefix === '/') ?? null;
}

/** Suspense/outlet key: one instance per lazy chunk (not per detail sub-route). */
export function routeChunkKey(pathname: string): string {
  return matchChunk(pathname)?.prefix ?? '/';
}

/**
 * Warm route chunk — fire-and-forget, deduped. Same `import()` fn as `React.lazy` in `App.tsx`
 * for heavy pages so Vite does not run two competing transforms.
 */
export function prefetchRoute(pathname: string): void {
  const entry = matchChunk(pathname);
  if (!entry) return;
  const key = entry.prefix;
  if (inflight.has(key)) return;
  const task = entry.load().finally(() => {
    if (inflight.get(key) === task) inflight.delete(key);
  });
  inflight.set(key, task);
}

/** No bulk prefetch on login — let navigation drive lazy loads only. */
export function prefetchCommonAdminRoutes(): void {
  /* intentionally empty */
}
